import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Users, Plus, Pencil, Trash2, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const EMPTY = { name: '', address: '', email: '', phone: '', contact_name: '' };
const field = 'h-11 bg-[#1E2530] border-white/20 text-white';

function CustomerForm({ initial, onSave, onCancel, busy }) {
  const [f, setF] = useState(initial || EMPTY);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  return (
    <div className="rounded-xl border border-[#F5A623]/40 bg-[#1E2530] p-3 space-y-2" data-testid="customer-form">
      <Input placeholder="Customer / company name *" value={f.name} onChange={set('name')} className={field} data-testid="customer-name" />
      <Textarea placeholder="Address (prints on the invoice)" value={f.address || ''} onChange={set('address')} className="bg-[#1E2530] border-white/20 text-white min-h-[60px]" data-testid="customer-address" />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Email (to send to)" type="email" value={f.email || ''} onChange={set('email')} className={field} data-testid="customer-email" />
        <Input placeholder="Phone" value={f.phone || ''} onChange={set('phone')} className={field} data-testid="customer-phone" />
      </div>
      <div className="flex gap-2">
        <Button variant="ghost" onClick={onCancel} className="flex-1 h-11 text-gray-300"><X className="w-4 h-4 mr-1" /> Cancel</Button>
        <Button onClick={() => onSave(f)} disabled={busy || f.name.trim().length < 2} className="flex-1 h-11 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="customer-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save</>}</Button>
      </div>
    </div>
  );
}

/** Address book for ad-hoc (non-LUX) invoices (brief §4/§5.3). Also reused inside the invoice wizard. */
export function SavedCustomersCard({ api, compact = false, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | customer
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/portal/invoice-customers'); setItems(r.data || []); }
    catch { /* ignore */ } // eslint-disable-line no-empty
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async (f) => {
    setBusy(true);
    try {
      const r = editing === 'new' ? await api.post('/portal/invoice-customers', f) : await api.put(`/portal/invoice-customers/${editing.id}`, f);
      toast.success('Customer saved'); setEditing(null); await load();
      if (editing === 'new' && onPick) onPick(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save customer'); }
    finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name} from your saved customers?`)) return;
    try { await api.delete(`/portal/invoice-customers/${c.id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const body = (
    <div className="space-y-2" data-testid="saved-customers">
      {loading ? <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div> : items.length === 0 && editing !== 'new' && (
        <div className="text-sm text-gray-500 py-2" data-testid="customers-empty">No saved customers yet. Add one to invoice a company other than LUX.</div>
      )}
      {items.map(c => editing?.id === c.id ? <CustomerForm key={c.id} initial={c} onSave={save} onCancel={() => setEditing(null)} busy={busy} /> : (
        <div key={c.id} className="rounded-xl border border-white/10 bg-[#1E2530] p-3 flex items-center gap-2" data-testid={`customer-${c.id}`}>
          <button type="button" onClick={() => onPick?.(c)} disabled={!onPick} className="flex-1 text-left min-h-[44px]" data-testid={`customer-pick-${c.id}`}>
            <div className="text-white font-semibold">{c.name}</div>
            <div className="text-xs text-gray-400 truncate">{[c.address, c.email].filter(Boolean).join(' · ') || 'No address yet'}</div>
          </button>
          <button type="button" onClick={() => setEditing(c)} className="w-10 h-10 rounded-lg text-gray-400 hover:text-white flex items-center justify-center" data-testid={`customer-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
          <button type="button" onClick={() => remove(c)} className="w-10 h-10 rounded-lg text-gray-500 hover:text-red-400 flex items-center justify-center" data-testid={`customer-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      {editing === 'new' ? <CustomerForm onSave={save} onCancel={() => setEditing(null)} busy={busy} /> : (
        <Button variant="outline" onClick={() => setEditing('new')} className="w-full h-12 border-dashed border-white/20 text-white bg-white/5" data-testid="customer-add-btn"><Plus className="w-4 h-4 mr-2" /> Add customer</Button>
      )}
    </div>
  );
  if (compact) return body;
  return (
    <Card className="bg-[#2A3240] border-white/10" data-testid="saved-customers-card">
      <CardHeader className="pb-3"><CardTitle className="text-white text-lg flex items-center gap-2"><Users className="w-5 h-5 text-purple-300" /> Saved customers</CardTitle></CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
